<script lang="ts" setup>
import { computed } from 'vue';
import { usePdfDownload } from '@Use/usePdfDownload';
import { useCompanyStore } from '@Stores/company.store';
import { useLocationStore } from '@Stores/location.store';
import { useRequestSessionStore } from '@Stores/requestSession.store';
import { useRequestDetailsStore } from '@Stores/requestDetails.store';
import { useDisplayName } from '@Shared';
import usePhoneNumber from '@Use/usePhoneNumber';
import { resolveCreditToPersonalAddress } from '@Use/useCreditToPersonalAddress';
import { formatAddressLines } from '@Use/useAddressFormatter';
import ExpandCollapseToggle from '@Components/ExpandCollapseToggle.vue';
import KeyValueDetails, {
    type KeyValueSection,
} from '@Components/KeyValueDetails.vue';
import type { WiringInstructions } from '@Types/WiringInstrucions.interface';

withDefaults(defineProps<{
    showTitle?: boolean;
}>(), {
    showTitle: true,
});

const companyStore = useCompanyStore();
const locationStore = useLocationStore();
const requestSessionStore = useRequestSessionStore();
const requestDetailsStore = useRequestDetailsStore();

const requestDetail = computed(() => requestDetailsStore.requestDetails);

const isRejected = computed(() => !!requestSessionStore.confirmRejection);

const { displayName: companyName } = useDisplayName(
    () => companyStore.company,
    () => locationStore.location,
);

const { rawPhone, formattedPhone: companyPhone } = usePhoneNumber(
    () => companyStore.company,
    () => locationStore.location,
);

const buildConfirmSections = (wi: WiringInstructions): KeyValueSection[] => {
    // personalAddress is the Confirm address field the agent and the participant
    // both write; creditTo.accountAddress is the bank-sourced slot, read only as a
    // fallback. See useCreditToPersonalAddress.
    //
    // Deliberately sourced from the refetched request rather than from the session
    // slot the review screen writes: this screen reports what was persisted. Using
    // the session copy would show the participant their edit even when the write
    // never landed.
    const creditToPersonalAddress = resolveCreditToPersonalAddress(wi);

    return [
        {
            title: 'Credit to',
            rows: [
                {
                    type: 'stacked',
                    label: 'Account holder',
                    values: [
                        wi.creditTo?.accountName,
                        ...formatAddressLines(creditToPersonalAddress),
                    ],
                    valueWeight: 'normal',
                },
                { type: 'labeled', label: 'Account number', value: wi.creditTo?.accountNumber },
            ],
        },
        {
            title: 'Beneficiary bank',
            rows: [
                { type: 'plain', value: wi.beneficiaryBank?.name ?? wi.bankName, valueWeight: 'normal' },
                {
                    type: 'labeled',
                    label: 'Routing number',
                    value: wi.beneficiaryBank?.routingNumber ?? wi.routingNumber,
                },
            ],
        },
    ];
};

const confirmSections = computed<KeyValueSection[]>(() =>
    requestDetail.value?.wiringInstructions
        ? buildConfirmSections(requestDetail.value.wiringInstructions)
        : [],
);

const rejectionSections = computed<KeyValueSection[]>(() => {
    const rejection = requestSessionStore.confirmRejection;
    if (!rejection) return [];

    const rows: KeyValueSection['rows'] = [
        { type: 'stacked', label: 'Reason', values: [rejection.reason], valueWeight: 'normal' },
    ];

    if (rejection.additionalDetails) {
        rows.push({
            type: 'stacked',
            label: 'Additional details',
            values: [rejection.additionalDetails],
            valueWeight: 'normal',
        });
    }

    return [{ rows }];
});

const { isPdfLoading, pdfError, downloadPdf } = usePdfDownload();
</script>

<template>
    <app-form-content-layout>
        <!-- Rejected state -->
        <template v-if="isRejected">
            <h1
                v-if="showTitle"
                class="confirm-complete__headline">
                Account details rejected
            </h1>

            <p class="confirm-complete__description">
                <strong>{{ companyName }}</strong> has been notified that you
                have rejected these account details.
            </p>

            <app-collapse
                class="confirm-complete__collapsible confirm-complete__rejection-details"
                data-id="confirm-complete-rejection-details-toggle"
                initial-state="opened">

                <template #trigger>
                    <expand-collapse-toggle
                        :is-open="true"
                        open-label="Rejection details"
                        data-id="confirm-complete-rejection-toggle" />
                </template>
                <key-value-details :sections="rejectionSections" />
            </app-collapse>
        </template>

        <!-- Approved state -->
        <template v-else>
            <h1
                v-if="showTitle"
                class="confirm-complete__headline">
                Thank you!
            </h1>

            <p class="confirm-complete__description">
                These account details have been shared securely with
                <strong>{{ companyName }}</strong>.
            </p>

            <app-collapse
                v-if="requestDetail?.wiringInstructions"
                class="confirm-complete__collapsible"
                data-id="confirm-complete-account-details-toggle"
                initial-state="closed">

                <template #trigger="slotProps">
                    <expand-collapse-toggle
                        :is-open="slotProps?.isOpen"
                        open-label="Hide account details"
                        closed-label="Show account details"
                        data-id="confirm-complete-toggle" />
                </template>
                <key-value-details :sections="confirmSections" />
            </app-collapse>

            <app-button
                expanded
                size="large"
                data-id="confirm-complete-download"
                :is-disabled="isPdfLoading"
                :is-loading="isPdfLoading"
                @onClick="downloadPdf">
                Download PDF
            </app-button>

            <p
                v-if="pdfError"
                class="confirm-complete__error"
                role="alert">
                {{ pdfError }}
            </p>
        </template>

        <p class="confirm-complete__contact">
            If you have additional questions please call:
            <template v-if="companyName || companyPhone">
                <br />
                <a
                    v-if="companyPhone"
                    :href="`tel:${rawPhone}`"
                    class="confirm-complete__contact-link">
                    {{ companyName }} - {{ companyPhone }}
                </a>
                <span
                    v-else
                    class="confirm-complete__contact-link">
                    {{ companyName }}
                </span>
            </template>
        </p>
    </app-form-content-layout>
</template>

<style lang="scss" scoped>
@use 'certifid-v2-client-hub-styles' as *;

.confirm-complete {
    &__headline {
        font-size: 1.625rem;
        font-weight: 700;
        line-height: 150%;
        color: $black-high;
        margin: 0;
    }

    &__description {
        font-size: 1rem;
        line-height: 150%;
        color: $black-high;
        margin: 0;
    }

    &__collapsible {
        display: flex;
        flex-direction: column;
        width: 100%;
        overflow: hidden;

        // this has to be locally scoped for now
        :deep(.trigger-indicator) {
            display: none;
        }
    }

    &__rejection-details {
        :deep(.trigger) {
            cursor: default;
            pointer-events: none;
        }

        :deep(.app-button__icon) {
            display: none;
        }
    }

    &__contact {
        font-size: 1rem;
        line-height: 150%;
        color: $black-high;
        text-align: center;
        margin: 0;
    }

    &__contact-link {
        color: $theme-primary;
        text-decoration: none;

        &:hover {
            text-decoration: underline;
        }
    }

    &__error {
        font-size: 0.875rem;
        color: $red;
        text-align: center;
        margin: 0;
    }
}
</style>
